import React, { useEffect, useState } from "react";
import {
  Button,
  CircularProgress,
  Box,
  TextField,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import DownloadIcon from "@mui/icons-material/Download";
import Swal from "sweetalert2";
import { pdf } from "@react-pdf/renderer";
import { saveAs } from "file-saver";

import api from "../../../api/api";
import TripBadgePDF from "./TripBadgePDF";
import TripBadgeTablePDF from "./TripBadgeTablePDF";

const TripBadgeDownloader = () => {
  const [trips, setTrips] = useState([]);
  const [selectedTrip, setSelectedTrip] = useState(null);

  const [loadingTrips, setLoadingTrips] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // ============================
  // LOAD TRIPS
  // ============================
  useEffect(() => {
    fetchTrips();
  }, []);

  const fetchTrips = async () => {
    try {
      setLoadingTrips(true);

      const res = await api.get("/shipping-trips/");

      const data = Array.isArray(res.data)
        ? res.data
        : res.data.results || [];

      // ✅ FORCE NEWEST FIRST
      const sortedTrips = [...data].sort(
        (a, b) =>
          new Date(b.created_at) - new Date(a.created_at)
      );

      setTrips(sortedTrips);
    } catch (error) {
      console.error(error);

      Swal.fire({
        icon: "error",
        title: "Failed",
        text: "Unable to load trips.",
      });
    } finally {
      setLoadingTrips(false);
    }
  };

  // ============================
  // FETCH SHIPMENTS
  // ============================
  const fetchShipments = async (tripId) => {
    const res = await api.get(
      `/trip-badges/?trip_id=${tripId}`
    );

    return res.data.results || [];
  };

  // ============================
  // DOWNLOAD BADGES PDF
  // ============================
  const downloadTripBadges = async () => {
    if (!selectedTrip) {
      Swal.fire({
        icon: "warning",
        title: "Trip Required",
        text: "Please select a trip first.",
      });
      return;
    }

    try {
      setDownloading(true);

      const shipments = await fetchShipments(
        selectedTrip.id
      );

      if (!shipments.length) {
        Swal.fire({
          icon: "info",
          title: "No Shipments",
          text: "No shipments found for this trip.",
        });
        return;
      }

      const blob = await pdf(
        <TripBadgePDF
          shipments={shipments}
          tripName={selectedTrip.name}
        />
      ).toBlob();

      saveAs(
        blob,
        `${selectedTrip.name
          .replace(/\s+/g, "_")
          .toLowerCase()}_badges.pdf`
      );

      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Trip badges downloaded successfully.",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error(error);

      Swal.fire({
        icon: "error",
        title: "Download Failed",
        text:
          error.response?.data?.error ||
          "Unable to generate trip badges.",
      });
    } finally {
      setDownloading(false);
    }
  };

  // ============================
  // DOWNLOAD TABLE PDF
  // ============================
  const downloadTripTable = async () => {
    if (!selectedTrip) {
      Swal.fire({
        icon: "warning",
        title: "Trip Required",
        text: "Please select a trip first.",
      });
      return;
    }

    try {
      setDownloading(true);

      const shipments = await fetchShipments(
        selectedTrip.id
      );

      if (!shipments.length) {
        Swal.fire({
          icon: "info",
          title: "No Shipments",
          text: "No shipments found for this trip.",
        });
        return;
      }

      const blob = await pdf(
        <TripBadgeTablePDF
          shipments={shipments}
          tripName={selectedTrip.name}
        />
      ).toBlob();

      saveAs(
        blob,
        `${selectedTrip.name
          .replace(/\s+/g, "_")
          .toLowerCase()}_table.pdf`
      );

      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Trip table downloaded successfully.",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error(error);

      Swal.fire({
        icon: "error",
        title: "Download Failed",
        text:
          error.response?.data?.error ||
          "Unable to generate trip table.",
      });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        gap: 2,
        alignItems: { xs: "stretch", md: "center" },
        mt: 2,
      }}
    >
      {/* ============================
          TRIP AUTOCOMPLETE (WITH SCROLL FIX)
      ============================ */}
      <Autocomplete
        options={trips}
        loading={loadingTrips}
        value={selectedTrip}
        onChange={(event, value) =>
          setSelectedTrip(value)
        }
        getOptionLabel={(option) =>
          option?.name || ""
        }
        isOptionEqualToValue={(option, value) =>
          option.id === value.id
        }
        sx={{ flex: 1 }}

        // ✅ SCROLL FIX (LIMIT HEIGHT AFTER 8 ITEMS)
        ListboxProps={{
          style: {
            maxHeight: 8 * 48, // ~8 rows height
            overflowY: "auto",
          },
        }}

        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <span>{option.name}</span>

              {/* ⭐ NEW LABEL FOR LATEST */}
              {trips[0]?.id === option.id && (
                <span
                  style={{
                    fontSize: 12,
                    color: "green",
                    fontWeight: 700,
                  }}
                >
                  NEW
                </span>
              )}
            </Box>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Search Trip"
            size="small"
            fullWidth
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loadingTrips && (
                    <CircularProgress size={18} />
                  )}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />

      {/* ============================
          ACTION BUTTONS
      ============================ */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          flexDirection: { xs: "column", md: "row" },
          width: { xs: "100%", md: "auto" },
        }}
      >
        <Button
          variant="contained"
          startIcon={
            downloading ? null : <DownloadIcon />
          }
          onClick={downloadTripBadges}
          disabled={downloading}
          sx={{
            minWidth: 220,
            fontWeight: 700,
            textTransform: "none",
          }}
        >
          {downloading ? (
            <>
              <CircularProgress
                size={18}
                sx={{ mr: 1, color: "#fff" }}
              />
              Generating...
            </>
          ) : (
            "Download Trip Badges"
          )}
        </Button>

        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={downloadTripTable}
          disabled={downloading}
          sx={{
            minWidth: 220,
            fontWeight: 700,
            textTransform: "none",
          }}
        >
          Download Trip Table
        </Button>
      </Box>
    </Box>
  );
};

export default TripBadgeDownloader;



// // TripBadgeDownloader.js

// import React, { useEffect, useState } from "react";
// import {
//   Button,
//   CircularProgress,
//   Box,
//   TextField,
// } from "@mui/material";
// import Autocomplete from "@mui/material/Autocomplete";
// import DownloadIcon from "@mui/icons-material/Download";
// import Swal from "sweetalert2";
// import { pdf } from "@react-pdf/renderer";
// import { saveAs } from "file-saver";

// import api from "../../../api/api";
// import TripBadgePDF from "./TripBadgePDF";
// import TripBadgeTablePDF from "./TripBadgeTablePDF";

// const TripBadgeDownloader = () => {
//   const [trips, setTrips] = useState([]);
//   const [selectedTrip, setSelectedTrip] = useState(null);

//   const [loadingTrips, setLoadingTrips] = useState(false);
//   const [downloading, setDownloading] = useState(false);

//   // ============================
//   // LOAD TRIPS
//   // ============================
//   useEffect(() => {
//     fetchTrips();
//   }, []);

//   const fetchTrips = async () => {
//     try {
//       setLoadingTrips(true);

//       const res = await api.get("/shipping-trips/");

//       const data = Array.isArray(res.data)
//         ? res.data
//         : res.data.results || [];

//       setTrips(data);
//     } catch (error) {
//       console.error(error);

//       Swal.fire({
//         icon: "error",
//         title: "Failed",
//         text: "Unable to load trips.",
//       });
//     } finally {
//       setLoadingTrips(false);
//     }
//   };

//   // ============================
//   // FETCH SHIPMENTS (COMMON)
//   // ============================
//   const fetchShipments = async (tripId) => {
//     const res = await api.get(
//       `/trip-badges/?trip_id=${tripId}`
//     );

//     return res.data.results || [];
//   };

//   // ============================
//   // DOWNLOAD BADGES PDF
//   // ============================
//   const downloadTripBadges = async () => {
//     if (!selectedTrip) {
//       Swal.fire({
//         icon: "warning",
//         title: "Trip Required",
//         text: "Please select a trip first.",
//       });
//       return;
//     }

//     try {
//       setDownloading(true);

//       const shipments = await fetchShipments(
//         selectedTrip.id
//       );

//       if (!shipments.length) {
//         Swal.fire({
//           icon: "info",
//           title: "No Shipments",
//           text: "No shipments found for this trip.",
//         });
//         return;
//       }

//       const blob = await pdf(
//         <TripBadgePDF
//           shipments={shipments}
//           tripName={selectedTrip.name}
//         />
//       ).toBlob();

//       saveAs(
//         blob,
//         `${selectedTrip.name
//           .replace(/\s+/g, "_")
//           .toLowerCase()}_badges.pdf`
//       );

//       Swal.fire({
//         icon: "success",
//         title: "Success",
//         text: "Trip badges downloaded successfully.",
//         timer: 2000,
//         showConfirmButton: false,
//       });
//     } catch (error) {
//       console.error(error);

//       Swal.fire({
//         icon: "error",
//         title: "Download Failed",
//         text:
//           error.response?.data?.error ||
//           "Unable to generate trip badges.",
//       });
//     } finally {
//       setDownloading(false);
//     }
//   };

//   // ============================
//   // DOWNLOAD TABLE PDF
//   // ============================
//   const downloadTripTable = async () => {
//     if (!selectedTrip) {
//       Swal.fire({
//         icon: "warning",
//         title: "Trip Required",
//         text: "Please select a trip first.",
//       });
//       return;
//     }

//     try {
//       setDownloading(true);

//       const shipments = await fetchShipments(
//         selectedTrip.id
//       );

//       if (!shipments.length) {
//         Swal.fire({
//           icon: "info",
//           title: "No Shipments",
//           text: "No shipments found for this trip.",
//         });
//         return;
//       }

//       const blob = await pdf(
//         <TripBadgeTablePDF
//           shipments={shipments}
//           tripName={selectedTrip.name}
//         />
//       ).toBlob();

//       saveAs(
//         blob,
//         `${selectedTrip.name
//           .replace(/\s+/g, "_")
//           .toLowerCase()}_table.pdf`
//       );

//       Swal.fire({
//         icon: "success",
//         title: "Success",
//         text: "Trip table downloaded successfully.",
//         timer: 2000,
//         showConfirmButton: false,
//       });
//     } catch (error) {
//       console.error(error);

//       Swal.fire({
//         icon: "error",
//         title: "Download Failed",
//         text:
//           error.response?.data?.error ||
//           "Unable to generate trip table.",
//       });
//     } finally {
//       setDownloading(false);
//     }
//   };

//   return (
//     <Box
//       sx={{
//         width: "100%",
//         display: "flex",
//         flexDirection: {
//           xs: "column",
//           md: "row",
//         },
//         gap: 2,
//         alignItems: {
//           xs: "stretch",
//           md: "center",
//         },
//         mt: 2,
//       }}
//     >
//       {/* ============================
//           TRIP SELECT
//       ============================ */}
//       <Autocomplete
//         options={trips}
//         loading={loadingTrips}
//         value={selectedTrip}
//         onChange={(event, value) =>
//           setSelectedTrip(value)
//         }
//         getOptionLabel={(option) =>
//           option?.name || ""
//         }
//         isOptionEqualToValue={(option, value) =>
//           option.id === value.id
//         }
//         sx={{
//           flex: 1,
//         }}
//         renderInput={(params) => (
//           <TextField
//             {...params}
//             label="Search Trip"
//             size="small"
//             fullWidth
//             InputProps={{
//               ...params.InputProps,
//               endAdornment: (
//                 <>
//                   {loadingTrips && (
//                     <CircularProgress
//                       size={18}
//                     />
//                   )}
//                   {params.InputProps
//                     .endAdornment}
//                 </>
//               ),
//             }}
//           />
//         )}
//       />

//       {/* ============================
//           ACTION BUTTONS
//       ============================ */}
//       <Box
//         sx={{
//           display: "flex",
//           gap: 2,
//           flexDirection: {
//             xs: "column",
//             md: "row",
//           },
//           width: {
//             xs: "100%",
//             md: "auto",
//           },
//         }}
//       >
//         {/* BADGES BUTTON */}
//         <Button
//           variant="contained"
//           startIcon={
//             downloading ? null : (
//               <DownloadIcon />
//             )
//           }
//           onClick={downloadTripBadges}
//           disabled={downloading}
//           sx={{
//             minWidth: 220,
//             fontWeight: 700,
//             textTransform: "none",
//           }}
//         >
//           {downloading ? (
//             <>
//               <CircularProgress
//                 size={18}
//                 sx={{ mr: 1, color: "#fff" }}
//               />
//               Generating...
//             </>
//           ) : (
//             "Download Trip Badges"
//           )}
//         </Button>

//         {/* TABLE BUTTON */}
//         <Button
//           variant="outlined"
//           startIcon={<DownloadIcon />}
//           onClick={downloadTripTable}
//           disabled={downloading}
//           sx={{
//             minWidth: 220,
//             fontWeight: 700,
//             textTransform: "none",
//           }}
//         >
//           Download Trip Table
//         </Button>
//       </Box>
//     </Box>
//   );
// };

// export default TripBadgeDownloader;